import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

function formatDate(dateValue) {
  if (!dateValue) return ''
  const date = new Date(dateValue)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit'
  })
}

function formatReadableDateTime(dateValue) {
  if (!dateValue) return ''
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return ''

  const parts = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).formatToParts(date)

  const get = (type) => parts.find(p => p.type === type)?.value || ''
  return `${get('month')} ${get('day')}, ${get('year')} at ${get('hour')}:${get('minute')} ${get('dayPeriod')} ${get('timeZoneName')}`.trim()
}

function renderDocumentBody(text) {
  const normalized = String(text || '').replace(/\r\n/g, '\n').trim()
  if (!normalized) return null

  const paragraphs = normalized.split(/\n\s*\n+/)

  return paragraphs.map((paragraph, pIndex) => {
    const lines = paragraph.split('\n')
    return (
      <p key={`p-${pIndex}`} style={{ margin: '0 0 1em', fontSize: 17, lineHeight: 1.75, color: '#1F2937' }}>
        {lines.map((line, lineIndex) => (
          <span key={`l-${lineIndex}`}>
            {line}
            {lineIndex < lines.length - 1 && <br />}
          </span>
        ))}
      </p>
    )
  })
}

function dedupeLettersById(rows) {
  const map = new Map()
  ;(rows || []).forEach((row) => {
    if (!row?.id) return
    if (!map.has(row.id)) map.set(row.id, row)
  })
  return Array.from(map.values())
}

function SignatureLine({
  label,
  signature,
  showDateValue = false,
  dateValueOverride = '',
  fallbackScriptName = '',
  fixedName,
  forceFixedName = false,
  subtitle,
}) {
  const effectiveDateValue = dateValueOverride || signature?.signed_at || ''
  const signedAt = formatReadableDateTime(effectiveDateValue)
  const displayName = forceFixedName ? (fixedName || label) : (signature?.signer_name || fixedName || label)

  return (
    <div className="signature-block" style={{ minWidth: 260 }}>
      <div style={{ minHeight: 72, display: 'flex', alignItems: 'flex-end', marginBottom: 2 }}>
        {signature?.signature_data ? (
          <img
            className="signature-image"
            src={signature.signature_data}
            alt={`${label} signature`}
            style={{ maxWidth: 220, height: 56, objectFit: 'contain' }}
          />
        ) : fallbackScriptName ? (
          <div style={{ fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive', fontSize: 34, color: '#1f2937', lineHeight: 1 }}>
            {fallbackScriptName}
          </div>
        ) : null}
      </div>

      <div style={{ borderBottom: '1px solid #111', marginBottom: 6, minHeight: 2 }} />

      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{displayName}</div>
      {subtitle && <div className="print-muted" style={{ fontSize: 12, marginBottom: 3 }}>{subtitle}</div>}
      {signedAt && (
        <div className="print-muted" style={{ fontSize: 12, marginBottom: 10 }}>
          Signed: {signedAt}
        </div>
      )}
      {!signedAt && <div style={{ minHeight: 26 }} />}

      <div style={{ borderBottom: '1px solid #111', marginBottom: 4, minHeight: 2 }} />
      <div className="print-muted" style={{ fontSize: 11 }}>
        Date: {showDateValue ? signedAt : ''}
      </div>
    </div>
  )
}

function SignatureCanvas({ disabled, clearToken, onChange }) {
  const canvasRef = useRef(null)
  const drawing = useRef(false)
  const hasInk = useRef(false)
  const lastPos = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = '#202326'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    drawing.current = false
    hasInk.current = false
    onChange(null)
  }, [clearToken, onChange])

  function getPos(e) {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height)
    }
  }

  function handlePointerDown(e) {
    if (disabled) return
    e.preventDefault()
    drawing.current = true
    lastPos.current = getPos(e)
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch (_) {}
  }

  function handlePointerMove(e) {
    if (!drawing.current || disabled) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const pos = getPos(e)
    if (Math.hypot(pos.x - lastPos.current.x, pos.y - lastPos.current.y) < 0.5) return
    ctx.beginPath()
    ctx.moveTo(lastPos.current.x, lastPos.current.y)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
    hasInk.current = true
    lastPos.current = pos
  }

  function handlePointerEnd() {
    if (!drawing.current) return
    drawing.current = false
    if (hasInk.current) onChange(canvasRef.current.toDataURL('image/png'))
  }

  return (
    <canvas
      ref={canvasRef}
      width={520}
      height={160}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerLeave={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      style={{
        width: '100%',
        height: 160,
        border: '2px solid var(--border)',
        borderRadius: 8,
        background: '#fff',
        cursor: disabled ? 'not-allowed' : 'crosshair',
        opacity: disabled ? 0.55 : 1,
        touchAction: 'none'
      }}
    />
  )
}

function SignerPanel({ label, signed, disabled, onSign }) {
  const [name, setName] = useState('')
  const [signatureData, setSignatureData] = useState(null)
  const [clearToken, setClearToken] = useState(0)
  const [savedForSubmission, setSavedForSubmission] = useState(false)

  const handleSignatureChange = useCallback((value) => {
    setSignatureData(value)
    setSavedForSubmission(false)
    onSign(null)
  }, [onSign])

  useEffect(() => {
    if (!signed) return
    setName('')
    setSignatureData(null)
  }, [signed])

  if (signed) {
    return (
      <div className="card" style={{ padding: 16, borderColor: '#6EE7B7', background: '#F0FDF4' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <strong style={{ fontSize: 13 }}>{label}</strong>
          <span className="badge badge-success">Signed</span>
        </div>
        <img src={signed.signature_data} alt={`${label} signature`} style={{ maxWidth: 220, height: 56, objectFit: 'contain' }} />
        <div style={{ marginTop: 8, borderTop: '1px solid #BBF7D0', paddingTop: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{signed.signer_name}</div>
          <div style={{ fontSize: 11, color: '#62615D' }}>{formatDate(signed.signed_at)}</div>
        </div>
      </div>
    )
  }

  const ready = name.trim().length > 1 && !!signatureData

  return (
    <div className="card" style={{ padding: 16 }}>
      <label className="label">{label}</label>
      <input
        className="input"
        value={name}
        disabled={disabled}
        onChange={e => { setName(e.target.value); setSavedForSubmission(false); onSign(null) }}
        placeholder="Type full legal name"
        style={{ marginBottom: 10 }}
      />
      <SignatureCanvas disabled={disabled} clearToken={clearToken} onChange={handleSignatureChange} />
      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <button
          className="btn btn-ghost"
          disabled={disabled}
          onClick={() => { setClearToken(v => v + 1); handleSignatureChange(null) }}
          style={{ fontSize: 12 }}>
          Clear
        </button>
        <button
          className="btn btn-primary"
          disabled={disabled || !ready}
          onClick={() => { onSign({ signerName: name.trim(), signatureData }); setSavedForSubmission(true) }}
          style={{ fontSize: 12 }}>
          Save Signature
        </button>
      </div>
      {savedForSubmission && <div role="status" style={{ marginTop: 8, fontSize: 12, color: '#065F46' }}>Signature ready. Choose Finalize Signatures to submit.</div>}
    </div>
  )
}

export default function EngagementSigningPage() {
  const { profile, user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [engagement, setEngagement] = useState(null)
  const [cpaProfile, setCpaProfile] = useState(null)
  const [cpaSignature, setCpaSignature] = useState(null)
  const [taxpayerSignature, setTaxpayerSignature] = useState(null)
  const [spouseSignature, setSpouseSignature] = useState(null)
  const [pendingTaxpayer, setPendingTaxpayer] = useState(null)
  const [pendingSpouse, setPendingSpouse] = useState(null)
  const [consent, setConsent] = useState(false)
  const [isJoint, setIsJoint] = useState(false)
  const submissionInProgress = useRef(false)

  const canFinalize = consent && !!(taxpayerSignature || pendingTaxpayer) && (!isJoint || !!(spouseSignature || pendingSpouse))

  async function loadSignatures(letterId) {
    const { data, error: sigError } = await supabase
      .from('engagement_signatures')
      .select('*')
      .eq('engagement_letter_id', letterId)
      .order('signed_at', { ascending: false })

    if (sigError) throw sigError

    const firstByRole = (role) => (data || []).find(row => row.signer_role === role) || null
    const cpa = firstByRole('cpa')
    const taxpayer = firstByRole('taxpayer')
    const spouse = firstByRole('spouse')

    setCpaSignature(cpa)
    setTaxpayerSignature(taxpayer)
    setSpouseSignature(spouse)
    if (spouse) setIsJoint(true)
  }

  useEffect(() => {
    async function load() {
      setLoading(true)
      setError('')

      try {
        const signedInUserId = profile?.id || user?.id
        if (!signedInUserId) {
          setError('Please sign in to access engagement signing.')
          setLoading(false)
          return
        }

        const queryParams = new URLSearchParams(window.location.search)
        const requestedLetterId = queryParams.get('engagementLetterId') || queryParams.get('id')

        const { data: clientRow, error: clientError } = await supabase
          .from('clients')
          .select('*')
          .eq('portal_user_id', signedInUserId)
          .maybeSingle()

        if (clientError) throw clientError
        if (!clientRow) {
          setError('No client profile is linked to this portal account yet.')
          setLoading(false)
          return
        }

        let letterQuery = supabase
          .from('engagement_letters')
          .select('*')
          .eq('client_id', clientRow.id)

        if (requestedLetterId) letterQuery = letterQuery.eq('id', requestedLetterId)

        const { data: letters, error: letterError } = await letterQuery
          .order('created_at', { ascending: false })
          .limit(1)

        if (letterError) throw letterError
        const letter = dedupeLettersById(letters || [])[0]
        if (!letter) {
          setError('No engagement letter is available for signature right now.')
          setLoading(false)
          return
        }

        setEngagement(letter)

        const { data: cpaProfileRow } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', letter.cpa_id)
          .maybeSingle()

        setCpaProfile(cpaProfileRow || null)

        await loadSignatures(letter.id)
      } catch (err) {
        setError(err.message || 'Unable to load engagement letter.')
      }

      setLoading(false)
    }

    load()
  }, [profile?.id, user?.id])

  // The saved body is the document the firm sent and the signing endpoint hashes.
  const renderedLetterBody = engagement?.body || ''

  const firmName = cpaProfile?.practice_name || cpaProfile?.full_name || 'Your tax professional'
  const firmAddress = [
    cpaProfile?.business_address,
    cpaProfile?.address,
    [cpaProfile?.street_address, cpaProfile?.apartment].filter(Boolean).join(', '),
    [
      [cpaProfile?.city, cpaProfile?.state].filter(Boolean).join(', '),
      cpaProfile?.zip_code,
    ].filter(Boolean).join(' '),
  ].filter(Boolean)[0] || 'Address on file'
  const firmPhone = cpaProfile?.phone || 'Phone on file'
  const cpaDisplayName = cpaSignature?.signer_name || cpaProfile?.full_name || 'Tax Professional'
  const taxProfessionalSignedAt = cpaSignature?.signed_at || ''
  const taxpayerLineName = taxpayerSignature?.signer_name || 'Taxpayer'
  const spouseLineName = spouseSignature?.signer_name || 'Spouse'
  const isSent = engagement?.status === 'sent'
  const isSigned = engagement?.status === 'signed'

  async function submitSignature(role, payload) {
    const { data: { session } } = await supabase.auth.getSession()
    const response = await fetch('/.netlify/functions/sign-engagement', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        engagementLetterId: engagement.id,
        signerRole: role,
        signerName: payload.signerName,
        signatureData: payload.signatureData
      })
    })

    const result = await response.json()
    if (!response.ok) throw new Error(result.error || 'Unable to save signature.')

    return result.data
  }

  async function handleFinalize() {
    if (!canFinalize || submissionInProgress.current) return
    submissionInProgress.current = true
    setSubmitting(true)
    setSubmitError('')

    try {
      // Submit spouse first so the taxpayer submission can trigger signed status for joint returns.
      if (isJoint && pendingSpouse && !spouseSignature) {
        await submitSignature('spouse', pendingSpouse)
      }

      const taxpayerPayload = pendingTaxpayer || (taxpayerSignature && {
        signerName: taxpayerSignature.signer_name,
        signatureData: taxpayerSignature.signature_data,
      })
      if (taxpayerPayload) {
        // Retrying an existing signature is safe and lets the endpoint finish a
        // letter whose signature persisted before its status update failed.
        await submitSignature('taxpayer', taxpayerPayload)
      }

      await loadSignatures(engagement.id)

      const { data: refreshedLetter, error: refreshError } = await supabase
        .from('engagement_letters')
        .select('*')
        .eq('id', engagement.id)
        .maybeSingle()

      if (refreshError) throw refreshError
      if (!refreshedLetter) throw new Error('Unable to confirm the engagement status. Please try again.')
      setEngagement(refreshedLetter)
      if (refreshedLetter.status !== 'signed') throw new Error('Your signatures are saved, but completion could not be confirmed. Please try finalizing again.')
      setPendingTaxpayer(null)
      setPendingSpouse(null)
    } catch (err) {
      setSubmitError(err.message || 'Unable to submit signatures.')
      // A preceding signer may already have been saved before a later request failed.
      // Refresh before retrying so the form reflects those persisted signatures.
      try { await loadSignatures(engagement.id) } catch (_) {}
    }

    setSubmitting(false)
    submissionInProgress.current = false
  }

  return (
    <div style={{ maxWidth: 920, margin: '0 auto' }}>
      <style>{`
        @media (max-width: 768px) {
          .engagement-signing-root .engagement-print-card {
            padding: 18px 14px !important;
          }
          .engagement-signing-root .engagement-action-card {
            padding: 14px !important;
          }
          .engagement-signing-root .engagement-sign-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media print {
          @page { margin: 1in; }
          html, body {
            background: #fff !important;
            color: #000 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          body * { visibility: hidden !important; }
          .print-area, .print-area * { visibility: visible !important; }
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            background: #fff !important;
            color: #000 !important;
          }
          .print-area .print-letter-body,
          .print-area .print-letter-body p {
            font-family: Georgia, "Times New Roman", serif !important;
            color: #000 !important;
            font-size: 12pt !important;
            line-height: 1.65 !important;
          }
          .print-area .signature-image {
            width: 2in !important;
            max-width: 2in !important;
            height: auto !important;
            object-fit: contain !important;
          }
          .print-area .signature-block {
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 0.25in;
          }
          .print-area .print-muted {
            color: #222 !important;
          }
          .print-area .print-grid {
            grid-template-columns: 1fr 1fr !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>

      {loading && (
        <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 88 }} />)}
        </div>
      )}

      {!loading && error && (
        <div className="card" style={{ padding: 16, background: '#FEE2E2', borderColor: '#FCA5A5', color: '#991B1B' }}>
          {error}
        </div>
      )}

      {!loading && !error && engagement && (
        <div className="engagement-signing-root">
          <div className="card print-area engagement-print-card" style={{ padding: '28px 34px', marginBottom: 16, background: '#fff' }}>
            <div style={{ borderBottom: '2px solid #111', paddingBottom: 12, marginBottom: 20 }}>
              <h1 style={{ fontSize: 30, marginBottom: 6 }}>{firmName}</h1>
              <div style={{ fontSize: 14, lineHeight: 1.4 }}>
                <div>{firmAddress}</div>
                <div>Phone: {firmPhone}</div>
              </div>
            </div>

            <h2 style={{ fontSize: 28, marginBottom: 6 }}>Engagement Letter</h2>
            <div className="print-muted" style={{ fontSize: 14, marginBottom: 18 }}>
              {engagement.title || 'Tax Preparation Engagement'}
            </div>

            <div className="print-letter-body" style={{ marginBottom: 24, fontFamily: 'Georgia, "Times New Roman", serif' }}>
              {renderDocumentBody(renderedLetterBody) || (
                <p style={{ margin: 0, fontSize: 16, color: '#6B7280' }}>No letter body available.</p>
              )}
            </div>

            <div style={{ borderTop: '1px solid #333', paddingTop: 14, marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, marginBottom: 12 }}>Tax Professional Signature Block</h3>
              <div className="print-grid" style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                <SignatureLine
                  label="Tax Professional"
                  signature={cpaSignature}
                  showDateValue={true}
                  dateValueOverride={taxProfessionalSignedAt}
                  fixedName={cpaDisplayName}
                  subtitle={firmName}
                />
              </div>
            </div>

            <div style={{ borderTop: '1px solid #333', paddingTop: 14 }}>
              <h3 style={{ fontSize: 18, marginBottom: 12 }}>Accepted By</h3>
              <div className="print-grid engagement-sign-grid" style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                <SignatureLine
                  label="Taxpayer"
                  signature={taxpayerSignature}
                  showDateValue={true}
                  fixedName={taxpayerLineName}
                />
                {isJoint && (
                  <SignatureLine
                    label="Spouse"
                    signature={spouseSignature}
                    showDateValue={true}
                    fixedName={spouseLineName}
                  />
                )}
              </div>

              {engagement.signed_at && (
                <div className="print-muted" style={{ marginTop: 12, fontSize: 12 }}>
                  Fully executed at {formatDate(engagement.signed_at)}
                </div>
              )}
            </div>
          </div>

          {isSent && (
            <div className="no-print card engagement-action-card" style={{ padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <h3 style={{ fontSize: 16 }}>Sign This Engagement</h3>
                <button className="btn btn-gold" onClick={() => window.print()}>
                  Print View
                </button>
              </div>

              <label style={{ display: 'flex', gap: 10, marginBottom: 10, fontSize: 13, color: '#4B5563' }}>
                <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
                <span>I consent to signing this engagement letter electronically and understand this signature is legally binding.</span>
              </label>

              <label style={{ display: 'flex', gap: 10, marginBottom: 16, fontSize: 13, color: '#4B5563' }}>
                <input type="checkbox" checked={isJoint} onChange={e => { setIsJoint(e.target.checked); if (!e.target.checked) setPendingSpouse(null) }} />
                <span>This is a joint return and requires spouse signature.</span>
              </label>

              <div className="engagement-sign-grid" style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', opacity: consent ? 1 : 0.6, pointerEvents: consent ? 'auto' : 'none' }}>
                <SignerPanel label="Taxpayer" signed={taxpayerSignature} disabled={!consent || submitting} onSign={setPendingTaxpayer} />
                {isJoint && <SignerPanel label="Spouse" signed={spouseSignature} disabled={!consent || submitting} onSign={setPendingSpouse} />}
              </div>

              {!!submitError && (
                <div style={{ marginTop: 12, background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
                  {submitError}
                </div>
              )}

              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, color: '#62615D' }}>
                  {canFinalize ? 'Ready to submit signatures.' : 'Complete all required signatures to submit.'}
                </div>
                <button className="btn btn-primary" disabled={!canFinalize || submitting} onClick={handleFinalize}>
                  {submitting ? 'Submitting…' : 'Finalize Signatures'}
                </button>
              </div>
            </div>
          )}

          {isSigned && (
            <div className="no-print card engagement-action-card" style={{ padding: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 14, color: '#065F46', fontWeight: 700 }}>
                Engagement letter completed and signed.
              </div>
              <button className="btn btn-gold" onClick={() => window.print()}>
                Print View
              </button>
            </div>
          )}

          {!isSent && !isSigned && (
            <div className="no-print card engagement-action-card" style={{ padding: 18, fontSize: 13, color: '#62615D' }}>
              This engagement letter is not available for client signing in its current status.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
